import { Any } from '~/common/utils';
import type { FileInput, ValidationResult } from '../types';
import { ZipParser, ZipEntryMetadata } from '../ZipParser';
import { validateZip } from './validateZip';

export async function validatePptx(input: FileInput): Promise<ValidationResult> {
  let entries: Record<string, Any>;
  let entriesArray: ZipEntryMetadata[] | undefined;

  if (input instanceof File) {
    // For File input, parse as ZIP
    const zipResult = await validateZip(input);
    if (!zipResult.valid) return zipResult;
    entries = zipResult.entries!;
  } else {
    // For ZIP entry input (PPTX inside another ZIP):
    // Stream into Blob chunks to avoid single large ArrayBuffer allocation
    // Note: Full decompressed content still needs to be in memory for ZIP parsing
    // (ZIP format requires random access to central directory at end of file)
    const chunks: Uint8Array[] = [];
    const streamResult = await input.parser.streamWithCallback(input.entry, (chunk) => {
      chunks.push(chunk);
    });

    if (!streamResult.valid) {
      return { valid: false, reason: streamResult.reason || 'Failed to extract PPTX' };
    }

    // Create Blob from chunks (more memory-efficient than single ArrayBuffer)
    const blob = new Blob(chunks);
    const parser = new ZipParser(blob);
    const parseResult = await parser.parse();
    entries = parseResult.namedEntries;
    entriesArray = parseResult.entries;
  }

  if (!entries['ppt/presentation.xml'])
    return { valid: false, reason: 'Missing ppt/presentation.xml — not a valid PPTX' };

  const hasSlides = Object.keys(entries).some((f) => f.startsWith('ppt/slides/slide'));
  if (!hasSlides) return { valid: false, reason: 'No slides found in PPTX' };

  // Return with pptxEntries cache (type assertion needed for union type)
  return {
    valid: true,
    pptxCache: entriesArray ? { [input.name || '']: entriesArray } : undefined,
  };
}
